import {
  annotation as csAnnotation,
  utilities as csToolUtils,
  cursors as csCursors,
  drawing as csDrawing,
  Enums as ToolEnums,
  state as csToolsState,
} from '@cornerstonejs/tools';
import {
  getEnabledElement,
  metaData,
  eventTarget,
  triggerEvent,
} from '@cornerstonejs/core';
import type { Types as CoreTypes } from '@cornerstonejs/core';
import {
  SPECTRAL_DOPPLER_OUT_OF_REGION,
  SPECTRAL_DOPPLER_TOOL_NAMES,
} from '@/models/spectralDoppler';
import {
  resolveSpectralRegion,
  buildCalibrationFromRegion,
} from './calibrationRegion';
import {
  deriveProbeValues,
  isPointInRegionBbox,
  clampToRegionBbox,
} from './calibrationMath';
import {
  formatDopplerTextLines,
  SpectralDopplerBaseTool,
  type UltrasoundRegionsMeta,
  type SpectralDopplerAnnotationData,
} from './SpectralDopplerBaseTool';
import { worldToPixel, clampWorldToRegionBbox } from './worldPixelCoords';
import { applySmartLabelAnchor } from '../labelPlacement';

const {
  state: annotationStateApi,
  locking: annotationLocking,
  visibility: annotationVisibility,
} = csAnnotation;
const { viewportFilters } = csToolUtils;
const { elementCursor } = csCursors;

interface ProbeAnnotationData extends SpectralDopplerAnnotationData {
  handles: { points: CoreTypes.Point3[]; textBox?: unknown };
  cachedStats: Record<string, unknown>;
  label: string;
  /** Hydrated AI probe — spectralValues are authoritative, never re-derived. */
  isAI?: boolean;
}

interface ProbeAnnotation {
  annotationUID: string;
  highlighted: boolean;
  invalidated: boolean;
  isLocked: boolean;
  isVisible: boolean;
  metadata: { toolName: string; referencedImageId?: string };
  data: ProbeAnnotationData;
}

function defaultGetTextLines(data: ProbeAnnotationData): string[] {
  // Hydrated AI markers set data.label ("AI: E"/"AI: A") so the two peak
  // markers are distinguishable; manual annotations leave it empty (no-op).
  const labelLines = data.label ? [data.label] : [];
  if (!data.spectralValues) return labelLines;
  return [
    ...labelLines,
    ...formatDopplerTextLines('probe', data.spectralValues),
  ];
}

class VelocityProbeTool extends SpectralDopplerBaseTool {
  static toolName = SPECTRAL_DOPPLER_TOOL_NAMES.PROBE;

  constructor(
    toolProps: Record<string, unknown> = {},
    defaultToolProps: Record<string, unknown> = {
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        getTextLines: defaultGetTextLines,
      },
    }
  ) {
    super(toolProps, defaultToolProps);

    this.addNewAnnotation = (evt: {
      detail: {
        element: HTMLDivElement;
        currentPoints: { world: CoreTypes.Point3 };
      };
    }): ProbeAnnotation | undefined => {
      const { element, currentPoints } = evt.detail;
      const worldPos = currentPoints.world;

      const enabled = getEnabledElement(element);
      if (!enabled) return undefined;
      const { viewport } = enabled;

      const imageId =
        (
          viewport as unknown as {
            getCurrentImageId?: () => string | undefined;
          }
        ).getCurrentImageId?.() ?? '';

      const regionsMeta = metaData.get('ultrasoundRegions', imageId) as
        | UltrasoundRegionsMeta
        | undefined;
      if (!regionsMeta) return undefined;
      const { region } = resolveSpectralRegion(regionsMeta.regions);
      if (!region) return undefined;

      const calibration = buildCalibrationFromRegion(region);
      const [px, py] = worldToPixel(viewport, worldPos);

      if (!isPointInRegionBbox(px, py, calibration)) {
        triggerEvent(eventTarget, SPECTRAL_DOPPLER_OUT_OF_REGION, {
          viewportId: viewport.id,
          imageId,
          px,
          py,
          bbox: {
            minX: calibration.bboxMinX,
            minY: calibration.bboxMinY,
            maxX: calibration.bboxMaxX,
            maxY: calibration.bboxMaxY,
          },
        });
        return undefined;
      }

      elementCursor.hideElementCursor(element);
      this.isDrawing = true;

      const annotation = (
        this as unknown as {
          createAnnotation: (
            evt: unknown,
            points: CoreTypes.Point3[]
          ) => ProbeAnnotation;
        }
      ).createAnnotation(evt, [[...worldPos] as CoreTypes.Point3]);

      annotation.data.spectralCalibration = calibration;
      annotation.data.spectralToolVariant = 'probe';
      annotation.invalidated = true;

      annotationStateApi.addAnnotation(annotation as unknown as never, element);

      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );

      this.editData = {
        annotation,
        viewportIdsToRender,
        handleIndex: 0,
        newAnnotation: true,
        hasMoved: false,
      };

      this._activateModify(element);
      (evt as { preventDefault?: () => void }).preventDefault?.();
      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      return annotation;
    };

    this._dragCallback = (evt: {
      detail: {
        element: HTMLDivElement;
        currentPoints: { world: CoreTypes.Point3 };
        deltaPoints?: { world: CoreTypes.Point3 };
      };
    }) => {
      if (!this.editData) return;
      this.isDrawing = true;
      const { annotation, handleIndex, movingTextBox, newAnnotation } =
        this.editData;
      const { data } = annotation as ProbeAnnotation;

      (
        this as unknown as {
          createMemo?: (
            element: HTMLDivElement,
            annotation: unknown,
            opts?: { newAnnotation?: boolean }
          ) => void;
        }
      ).createMemo?.(evt.detail.element, annotation, { newAnnotation });

      if (movingTextBox) {
        const delta = evt.detail.deltaPoints?.world;
        if (delta) {
          const tb = (
            data.handles as {
              textBox?: { worldPosition: number[]; userMoved?: boolean };
            }
          ).textBox;
          if (tb) {
            tb.worldPosition[0] += delta[0];
            tb.worldPosition[1] += delta[1];
            tb.worldPosition[2] += delta[2];
            tb.userMoved = true;
          }
        }
      } else if (handleIndex !== undefined) {
        const { world } = evt.detail.currentPoints;
        const enabled = getEnabledElement(evt.detail.element);
        const cal = data.spectralCalibration;
        const clampedWorld =
          enabled && cal
            ? clampWorldToRegionBbox(enabled.viewport, world, cal)
            : ([...world] as CoreTypes.Point3);
        data.handles.points[handleIndex] = clampedWorld;
        (annotation as { invalidated: boolean }).invalidated = true;
      }

      this.editData.hasMoved = true;
      csToolUtils.triggerAnnotationRenderForViewportIds(
        this.editData.viewportIdsToRender
      );
    };

    this._endCallback = (evt: { detail: { element: HTMLDivElement } }) => {
      const { element } = evt.detail;
      if (!this.editData) return;
      const { annotation, viewportIdsToRender, newAnnotation } = this.editData;

      this._deactivateModify(element);
      elementCursor.resetElementCursor(element);

      // Finalize: snapshot values from current handle position so downstream
      // capture has final numbers even if the user never dragged.
      const enabled = getEnabledElement(element);
      const viewport = enabled?.viewport;
      const probe = annotation as ProbeAnnotation;
      if (viewport && probe.data.spectralCalibration) {
        const cal = probe.data.spectralCalibration;
        const [px, py] = worldToPixel(viewport, probe.data.handles.points[0]);
        const [cpx, cpy] = clampToRegionBbox(px, py, cal);
        probe.data.spectralValues = deriveProbeValues(cpx, cpy, cal);
        probe.data.spectralPixelPoints = [[cpx, cpy]];
        probe.invalidated = false;
      }

      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      if (newAnnotation) {
        annotationStateApi.triggerAnnotationCompleted(
          annotation as unknown as never
        );
      }

      (this as unknown as { doneEditMemo?: () => void }).doneEditMemo?.();

      this.editData = null;
      this.isDrawing = false;
    };

    this.cancel = (element: HTMLDivElement): string | undefined => {
      if (!this.isDrawing || !this.editData) return undefined;
      const { annotation } = this.editData;
      this.isDrawing = false;
      this._deactivateModify(element);
      elementCursor.resetElementCursor(element);
      const uid = (annotation as ProbeAnnotation).annotationUID;
      if (uid) annotationStateApi.removeAnnotation(uid);
      this.editData = null;
      return uid;
    };

    this._activateModify = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = true;
      element.addEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_CLICK,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_TAP,
        this._endCallback as EventListener
      );
    };

    this._deactivateModify = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = false;
      element.removeEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_CLICK,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_TAP,
        this._endCallback as EventListener
      );
    };

    this.isPointNearTool = (
      element: HTMLDivElement,
      annotation: ProbeAnnotation,
      canvasCoords: CoreTypes.Point2,
      proximity: number
    ): boolean => {
      const enabled = getEnabledElement(element);
      if (!enabled) return false;
      const { viewport } = enabled;
      const [point] = annotation.data.handles.points;
      if (!point) return false;
      const [cx, cy] = viewport.worldToCanvas(point);
      const dx = cx - canvasCoords[0];
      const dy = cy - canvasCoords[1];
      return Math.hypot(dx, dy) <= proximity;
    };

    this.toolSelectedCallback = (
      evt: { detail: { element: HTMLDivElement } },
      annotation: ProbeAnnotation
    ) => {
      const { element } = evt.detail;
      // eslint-disable-next-line no-param-reassign
      annotation.highlighted = true;
      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );
      this.editData = {
        annotation,
        viewportIdsToRender,
        handleIndex: 0,
        movingTextBox: false,
      };
      this._activateModify(element);
      elementCursor.hideElementCursor(element);
      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
    };

    this.handleSelectedCallback = (
      evt: { detail: { element: HTMLDivElement }; preventDefault?: () => void },
      annotation: ProbeAnnotation,
      handle: unknown
    ) => {
      const { element } = evt.detail;
      // eslint-disable-next-line no-param-reassign
      annotation.highlighted = true;
      const movingTextBox = !!(handle as { worldPosition?: unknown })
        ?.worldPosition;
      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );
      this.editData = {
        annotation,
        viewportIdsToRender,
        handleIndex: movingTextBox ? undefined : 0,
        movingTextBox,
      };
      this._activateModify(element);
      elementCursor.hideElementCursor(element);
      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      evt.preventDefault?.();
    };

    this.renderAnnotation = (
      enabledElement: CoreTypes.IEnabledElement,
      svgDrawingHelper: unknown
    ): boolean => {
      let rendered = false;
      const { viewport } = enabledElement;
      const { element } = viewport;

      let annotations = annotationStateApi.getAnnotations(
        this.getToolName(),
        element
      ) as unknown as ProbeAnnotation[] | undefined;
      if (!annotations?.length) return rendered;
      annotations = this.filterInteractableAnnotationsForElement(
        element,
        annotations as unknown as Record<string, unknown>[]
      ) as unknown as ProbeAnnotation[];
      if (!annotations?.length) return rendered;

      const styleSpecifier = {
        toolGroupId: this.toolGroupId,
        toolName: this.getToolName(),
        viewportId: viewport.id,
      };

      for (let i = 0; i < annotations.length; i += 1) {
        const annotation = annotations[i];
        const { annotationUID, data } = annotation;
        const { points } = data.handles;
        if (
          points?.length &&
          annotationVisibility.isAnnotationVisible(annotationUID)
        ) {
          // Recompute values on invalidation (initial create + every drag).
          // The handle world point is already clamped to the bbox during drag,
          // so the derived pixel is guaranteed inside the region.
          // AI probes carry the backend's reported peak velocity; keep it (a
          // 1px y-error re-derives to ~0.01 m/s off the report) (AP-616) —
          // UNLESS the user is actively dragging THIS probe, which is a reader
          // taking ownership: recompute live so the value tracks the crosshair
          // and the adopt→manual flip has a fresh number (mirrors Length).
          const beingEdited =
            (this.editData as { annotation?: unknown } | null)?.annotation ===
            annotation;
          if (
            annotation.invalidated &&
            data.spectralCalibration &&
            (!data.isAI || beingEdited)
          ) {
            const cal = data.spectralCalibration;
            const [px, py] = worldToPixel(viewport, points[0]);
            data.spectralValues = deriveProbeValues(px, py, cal);
            data.spectralPixelPoints = [[px, py]];
            annotation.invalidated = false;
            annotationStateApi.triggerAnnotationModified(
              annotation as unknown as never,
              element,
              ToolEnums.ChangeTypes.StatsUpdated
            );
          }

          const styleCtx = { ...styleSpecifier, annotationUID };
          const { color, lineWidth } = this.getAnnotationStyle({
            annotation,
            styleSpecifier: styleCtx,
          }) as { color: string; lineWidth: number };

          const canvas = viewport.worldToCanvas(points[0]);
          const size = 8;

          csDrawing.drawLine(
            svgDrawingHelper as never,
            annotationUID,
            `${annotationUID}-xh-h`,
            [canvas[0] - size, canvas[1]] as CoreTypes.Point2,
            [canvas[0] + size, canvas[1]] as CoreTypes.Point2,
            { color, width: lineWidth }
          );
          csDrawing.drawLine(
            svgDrawingHelper as never,
            annotationUID,
            `${annotationUID}-xh-v`,
            [canvas[0], canvas[1] - size] as CoreTypes.Point2,
            [canvas[0], canvas[1] + size] as CoreTypes.Point2,
            { color, width: lineWidth }
          );

          if (!annotationLocking.isAnnotationLocked(annotationUID)) {
            csDrawing.drawHandles(
              svgDrawingHelper as never,
              annotationUID,
              `${annotationUID}-handles`,
              [canvas],
              { color }
            );
          }

          const getTextLines = this.configuration?.getTextLines as
            | ((data: ProbeAnnotationData) => string[])
            | undefined;
          const textLines = getTextLines?.(data) ?? [];

          // Auto-place the label only when it's actually shown.
          if (textLines.length) {
            applySmartLabelAnchor({
              annotation: annotation as unknown as {
                data: { handles: { textBox?: unknown } };
              },
              geometryCanvasPoints: [canvas],
              spot: { point: canvas, side: 'right' },
              lines: textLines,
              viewport: viewport as unknown as {
                element: HTMLElement;
                canvasToWorld: (p: CoreTypes.Point2) => CoreTypes.Point3;
              },
            });
          }

          this.renderLinkedTextBoxAnnotation({
            enabledElement,
            svgDrawingHelper,
            annotation,
            styleSpecifier: styleCtx,
            textLines,
            canvasCoordinates: [canvas],
          });

          rendered = true;
        }
      }

      return rendered;
    };
  }
}

export default VelocityProbeTool;
